import { APP_INITIALIZER, ErrorHandler, Injectable, Provider, inject } from '@angular/core';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular';

import { constants } from '@shared/constants';
import { getEnvironment } from '@shared/services';

// Imported by relative path, not through the @shared/services barrel: this file is itself part of
// that barrel, and the cycle would leave the symbol undefined at decoration time.
import { ChunkLoadRecoveryService, ChunkRecoveryStage, isChunkLoadFailure } from '../chunk-load-recovery/chunk-load-recovery.service';

export interface SentryHttpFailure {
  requestURL: string;
  pageURL: string;
  errorMessage: string;
  errorStatusCode: number;
  timestamp: string;
}

/** The subset of Sentry's Partial<ScopeContext> this capture uses. Declared locally because
 * @sentry/angular does not re-export ScopeContext. */
export interface SentryCaptureContext {
  level: 'error' | 'warning';
  fingerprint: string[];
  tags: Record<string, string>;
  contexts: Record<string, Record<string, unknown>>;
}

const UUID_SEGMENT = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const LONG_NUMERIC_SEGMENT = /^\d{6,}$/;

export const initializeSentry = (release: string) => {
  const isLocalhost = location.hostname === 'localhost';

  Sentry.init({
    dsn: constants.SENTRY_DSN,
    environment: getEnvironment().name,
    release,
    attachStacktrace: true,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    enabled: !isLocalhost,
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    // integrations: [
    //   new BrowserTracing({
    //     tracingOrigins: ['https://*.rocketkor-nonprod.net', 'https://*.rocketkor.net'],
    //     routingInstrumentation: Sentry.routingInstrumentation,
    //   }),
    //   new Sentry.Integrations.Breadcrumbs({
    //     console: false,
    //   }),
    //   new Sentry.Replay(),
    // ],
    tracesSampleRate: 0.5,
  });
};

/**
 * Wraps Sentry's ErrorHandler so a failed build-asset import is recovered instead of merely
 * reported. Every other error is delegated untouched, so the capture behaviour established in
 * KOR-15101 is unchanged.
 */
@Injectable()
export class ChunkAwareErrorHandler implements ErrorHandler {
  private readonly delegate = Sentry.createErrorHandler({ showDialog: false });
  private readonly recovery = inject(ChunkLoadRecoveryService);
  private readonly router = inject(Router);

  public handleError(error: unknown): void {
    if (!isChunkLoadFailure(error)) {
      this.delegate.handleError(error);

      return;
    }

    // canRetry: false - this error did not come from a navigation, so re-running the current
    // route would not re-attempt the import that failed. Go straight to the reload.
    const stage = this.recovery.recover(this.router.url, { canRetry: false });

    captureChunkLoadRecovery(stage, this.router.url);

    // A reload is about to discard this document, and a retry is expected to succeed - neither is
    // a fault worth reporting as an unhandled error. Only exhaustion reaches the normal handler.
    if (stage === 'gave-up') {
      this.delegate.handleError(error);
    }
  }
}

/* Sentry providers configuration */
export const sentryProviders: () => Provider[] = () => {
  return [
    {
      provide: ErrorHandler,
      useClass: ChunkAwareErrorHandler,
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => ({}),
      deps: [Sentry.TraceService],
      multi: true,
    },
    // Angular v19
    // provideAppInitializer(() => {
    //     const initializerFn = (() => () => ({}))(inject(Sentry.TraceService));
    //     return initializerFn();
    //   }),
  ];
};

/**
 * Collapses the volatile parts of a request URL so that failures on the same endpoint group into a
 * single Sentry issue instead of one per account or transaction id. Also keeps ids out of the
 * indexed fields (fingerprint and tags) - the unredacted URL stays in the structured context.
 */
export const normalizeRoute = (url: string): string => {
  const path = (url || '').split(/[#?]/)[0].replace(/^[a-z]+:\/\/[^/]+/i, '');

  return path
    .split('/')
    .map((segment) => (UUID_SEGMENT.test(segment) || LONG_NUMERIC_SEGMENT.test(segment) ? ':id' : segment))
    .join('/');
};

/**
 * Builds the Error and the per-event capture context. Kept pure and separate from the SDK call so
 * the grouping rules are unit-testable without mocking the Sentry module namespace.
 */
export const buildSentryCapture = (failure: SentryHttpFailure): { error: Error; captureContext: SentryCaptureContext } => {
  const route = normalizeRoute(failure.requestURL);
  const status = String(failure.errorStatusCode);

  const error = new Error(`HTTP ${status} ${route}: ${failure.errorMessage}`);

  error.name = `HttpError${status}`;

  return {
    error,
    captureContext: {
      level: failure.errorStatusCode >= 500 ? 'error' : 'warning',
      fingerprint: [status, route],
      tags: { StatusCode: status, route },
      // AC3 keeps the payload attached, but with both URLs normalized: request and page paths carry
      // account and customer ids, and those should not cross to a third-party service.
      contexts: { httpFailure: { ...failure, requestURL: route, pageURL: normalizeRoute(failure.pageURL) } },
    },
  };
};

export const captureSentryException = (failure: SentryHttpFailure) => {
  const { error, captureContext } = buildSentryCapture(failure);

  Sentry.captureException(error, captureContext);
};

/**
 * Records what chunk-load recovery did. Grouped under one fixed fingerprint so it stays a single
 * issue, which is what makes KOR-15102's "the old issues go quiet" measurable: the deploy-time
 * cluster moves here instead of disappearing entirely.
 */
export const captureChunkLoadRecovery = (stage: ChunkRecoveryStage, url: string) => {
  const route = normalizeRoute(url);

  Sentry.captureMessage(`Chunk load recovery: ${stage}`, {
    level: stage === 'gave-up' ? 'error' : 'info',
    fingerprint: ['chunk-load-recovery'],
    tags: { chunkRecoveryStage: stage, route },
  });
};
